import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { ErrorBoundary } from "@sentry/react";
import React, { useMemo } from "react";
import { RootStateOrAny, useSelector } from "react-redux";
import { Tooltip } from "react-tippy";
import { ErrorMsg } from "../../../../components/AppMessages";
import LoadingEllipsis from "../../../../components/Loading/LoadingEllipsis";
import { DefaultTable } from "../../../../components/Tables/subs";

const OtherTouchpointsTable = () => {
  const { Current: currentOtherTouchpoints, YoY: yoyOtherTouchpoints } = useSelector(
    (state: RootStateOrAny) => state.voc.rating_per_touchpoint.other_touchpoints
  );
  const { rating_per_touchpoint_other_touchpoints: loading } = useSelector((state: RootStateOrAny) => state.loading);

  const columns = useMemo(
    () => [
      {
        id: "touchpoints",
        Header: "Touchpoints",
        accessor: "touchpoint",
        enableRowSpan: true,
        sort: false,
        disableSortBy: true,
      },
      {
        id: "metric",
        Header: "Metric",
        accessor: "metric",
        sort: false,
        disableSortBy: true,
      },
      {
        id: "value",
        Header: "Selected period",
        accessor: "value",
        sort: false,
        disableSortBy: true,
        Cell: (cell: { value: number; row: any }) => {
          const { value } = cell;
          const metric = cell.row.original.metric;
          return (
            <div className={metric == "Rate" ? `${value >= 4 ? "textGreen" : value >= 3.9 ? "textAmber" : "textRed"}` : ""}>
              {value ? value.toLocaleString() : value != 0 ? value : ""}
            </div>
          );
        },
      },
      {
        id: "significant",
        Header: () => {
          return (
            <div style={{ display: "flex" }}>
              <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" />
              <Tooltip
                position="right"
                className={"normal_tippy"}
                trigger="mouseenter"
                animation="none"
                interactive={true}
                delay={1}
                hideDelay={1}
                duration={1}
                title={
                  "Watch out for low statistical significance. A 'down arrow' signifies a low volume of respondents (lower than 16 per month)"
                }
                size={"small"}
              >
                <span className="info_tooltip" />
              </Tooltip>
            </div>
          );
        },
        accessor: "significant",
        sort: false,
        disableSortBy: true,
        Cell: (cell: any) => {
          const showIcon = cell.row.values.significant;
          return !showIcon ? <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" /> : null;
        },
      },
      {
        id: "ytd",
        Header: "Previous period",
        accessor: "yoy_value",
        sort: false,
        disableSortBy: true,
        Cell: (cell: { value: number; row: any }) => {
          const { value } = cell;
          return <div> {value ? value.toLocaleString() : value != 0 ? value : ""}</div>;
        },
      },
      {
        id: "yoy_significant",
        Header: () => {
          return (
            <div style={{ display: "flex" }}>
              <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" />
              <Tooltip
                position="right"
                className={"normal_tippy"}
                trigger="mouseenter"
                animation="none"
                interactive={true}
                delay={1}
                hideDelay={1}
                duration={1}
                title={
                  "Watch out for low statistical significance. A 'down arrow' signifies a low volume of respondents  (lower than 16 per month)"
                }
                size={"small"}
              >
                <span className="info_tooltip" />
              </Tooltip>
            </div>
          );
        },
        accessor: "yoy_significant",
        sort: false,
        disableSortBy: true,
        Cell: (cell: any) => {
          const showIcon = cell.row.values.yoy_significant;
          return !showIcon ? <ArrowDownwardIcon htmlColor="#A9A9A9" className="rtp-significance-arrow" /> : null;
        },
      },
    ],
    [currentOtherTouchpoints]
  );

  const tableData = useMemo(() => {
    const data: any = [];
    if (currentOtherTouchpoints && yoyOtherTouchpoints) {
      currentOtherTouchpoints.forEach((item: any) => {
        const yoyEquivalent = yoyOtherTouchpoints.find((yoyItem: any) => yoyItem.touchpoint == item.touchpoint);
        data.push(
          {
            touchpoint: item.touchpoint,
            metric: "Rate",
            value: item.rate,
            significant: true,
            yoy_value: yoyEquivalent?.pts_rate,
            yoy_significant: true,
          },
          {
            touchpoint: item.touchpoint,
            metric: "# answers",
            value: item.answers,
            significant: item.significant,
            yoy_value: yoyEquivalent?.pts_answers,
            yoy_significant: yoyEquivalent?.significant,
          }
        );
      });
    }
    return data;
  }, [currentOtherTouchpoints, yoyOtherTouchpoints]);

  return (
    <ErrorBoundary fallback={<ErrorMsg />}>
      {tableData && tableData.length > 0 && (
        <div className="row grid">
          <div className="tile grid-col-span-6 min-h-15">
            <div className="tileHeader">Other touchpoints</div>
            {loading ? (
              <LoadingEllipsis isLoading={loading} />
            ) : (
              <>
                <DefaultTable
                  columns={columns}
                  data={tableData}
                  id={"otherTouchpointsTable"}
                  isChildComponent={false}
                  initialSortColId={{ id: "touchpoints", desc: false }}
                  hiddenCols={[]}
                  manualSortBy={true}
                  extraClass="other-touchpoints-table"
                />
              </>
            )}
          </div>
        </div>
      )}
    </ErrorBoundary>
  );
};

export default OtherTouchpointsTable;
